import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";

export type CombatSpriteKind = "salt" | "tome";
const ART = {
  salt: { file: "gandhi-salt-flight", frames: 8, rows: 2, fps: 18, size: 1.65 },
  tome: { file: "dante-tome-uppercut", frames: 12, rows: 3, fps: 22, size: 2.6 },
} as const;

/** Dedicated PixelLab spell art. The caller supplies the authoritative position. */
export class CombatSpriteView {
  private readonly scene: Scene;
  private readonly kind: CombatSpriteKind;
  private readonly mesh: Mesh;
  private readonly material: StandardMaterial;
  private readonly texture: Texture;
  private readonly right = new Vector3();
  private readonly forward = new Vector3();
  private age = 0;

  constructor(scene: Scene, kind: CombatSpriteKind) {
    this.scene=scene; this.kind=kind;
    const art=ART[kind];
    this.mesh=CreatePlane(`spell-${kind}`,{size:1},scene);
    this.mesh.billboardMode=Mesh.BILLBOARDMODE_Y;
    this.mesh.isPickable=false;
    this.texture=new Texture(`/proto2d/combat-v2/${art.file}.png`,scene,true,true,Texture.NEAREST_SAMPLINGMODE);
    this.texture.hasAlpha=true;
    this.texture.wrapU=this.texture.wrapV=Texture.CLAMP_ADDRESSMODE;
    this.material=new StandardMaterial(`spell-${kind}`,scene);
    this.material.diffuseTexture=this.texture;
    this.material.useAlphaFromDiffuseTexture=true;
    this.material.emissiveColor=new Color3(0.72,0.67,0.6);
    this.material.specularColor=Color3.Black();
    this.material.backFaceCulling=false;
    this.mesh.material=this.material;
  }

  update(x:number,y:number,z:number,fx:number,fz:number,dt:number):void {
    this.age+=dt;
    const camera=this.scene.activeCamera;
    if(!camera)return;
    const art=ART[this.kind];
    camera.getDirectionToRef(Vector3.Right(),this.right);
    camera.getDirectionToRef(Vector3.Forward(),this.forward);
    const tilt=Math.max(0.55,Math.hypot(this.forward.x,this.forward.z));
    const mirrored=fx*this.right.x+fz*this.right.z<0;
    const frame=this.kind==="salt" ? Math.floor(this.age*art.fps)%art.frames : Math.min(art.frames-1,Math.floor(this.age*art.fps));
    this.texture.uScale=(mirrored?-1:1)/4;
    this.texture.vScale=1/art.rows;
    this.texture.uOffset=((frame%4)+(mirrored?1:0))/4;
    this.texture.vOffset=1-(Math.floor(frame/4)+1)/art.rows;
    this.mesh.scaling.set(art.size,art.size/tilt,1);
    this.mesh.position.set(x,y+(this.kind==="tome"?this.age*2.5:0),z);
    this.material.alpha=Math.min(1,this.age/0.04)*(this.kind==="tome"?Math.min(1,Math.max(0,(0.62-this.age)/0.16)):1);
  }

  dispose():void {this.mesh.dispose(false,true);}
}
